import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import http from '../../../utils/http';
import { useCallback } from 'react';

export const useUserSettings = <Value = any>(
  name: string,
  fallback?: Value,
) => {
  const queryClient = useQueryClient();
  const userSettingsQueryKey = ['api_user_settings', name] as const;

  const { data, ...query } = useQuery({
    queryKey: userSettingsQueryKey,
    queryFn: async () => {
      const result = await http.get<never, { value: Value }>(
        `/api/user-settings/${name}`,
      );

      return {
        value: result.value || fallback,
      };
    },
  });

  const mutation = useMutation<any, unknown, Record<string, any>>({
    mutationFn: (data) => http.put('/api/user-settings', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userSettingsQueryKey });
    },
  });

  const update = useCallback(
    async (value: any) => {
      return mutation.mutateAsync({ [name]: value });
    },
    [mutation.mutateAsync],
  );

  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isUpdating: mutation.isPending,
    value: data?.value || fallback,
    update,
  };
};
